import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import MarketingNavbar from "../marketingNav.js";
import LoadingSpinner from "../loadingSpinner.js";
import useDrillView from "./useDrillView.tsx";
import { useUser } from "./useUser.tsx";
import { auth, db } from "../../services/firebase.js";
import { useCollaboration } from "./useCollaboration.tsx";
import { useDrillDocument } from "./useDrillDocument.tsx";
import useFieldEditor from "./useFieldEditor.tsx";
import usePlayback from "./usePlayback.tsx";
import { useKeyboard } from "./useKeyboard.tsx";
import usePerformerManipulation from "./usePerformerManipulation.tsx";
import Drill from "@drillflo/drillcore/lib/Drill";
import View from "@drillflo/drillcore/lib/2d/View.ts";
import { FieldEditorData } from "./useFieldEditor.tsx";
import { PlaybackData } from "./usePlayback.tsx";
import { PerformerPaintingProps } from "./useDrillView.tsx";
import { CollaborationReturn } from "./useCollaboration.tsx";
import { UserData } from "./useUser.tsx";
import { DrillDocumentData } from "./useDrillDocument.tsx";
import Engine from "@drillflo/drillcore/lib/2d/Engine";
import { ViewOptions } from "@drillflo/drillcore/lib/2d/View";
import ExportEngine from "@drillflo/drillcore/lib/2d/ExportEngine";
import Page from "@drillflo/drillcore/lib/Page";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";

interface FieldEditorContextType {
  // Field Editor State
  fieldEditorData: FieldEditorData;
  setFieldEditorData: React.Dispatch<React.SetStateAction<FieldEditorData>>;

  // Drill View State
  drill: Drill | null;
  engine: Engine | null;
  view: View | null;
  DrillCanvas: JSX.Element;
  performerPaintingProps: PerformerPaintingProps;
  setPerformerPaintingProps: (
    value: React.SetStateAction<PerformerPaintingProps>,
  ) => void;
  viewOptions: ViewOptions;
  updateViewOptions: (newOptions: Partial<ViewOptions>) => Promise<void>;
  confirmShape: () => void;
  cancelShape: () => void;
  forceViewRecreate: () => void;
  uploadCanvasSnapshot: (canvasId: string) => Promise<void>;

  // Playback State
  playbackState: PlaybackData;
  setPlaybackState: React.Dispatch<React.SetStateAction<PlaybackData>>;
  handlePageChange: (page: number) => void;
  handleCountChange: (count: number) => void;
  handlePlayDrill: () => void;
  handleStopDrill: () => void;

  // Collaboration State
  collaborationReturn: CollaborationReturn;

  // Drill Document State
  drillDocumentData: DrillDocumentData | null;
  updateDrillDocumentData: (data: Partial<DrillDocumentData>) => void;

  // User State
  userData: UserData | null;

  // Performer Manipulation
  alignPerformersToLeft: () => void;
  alignPerformersToTop: () => void;
  alignPerformersHorizontally: () => void;
  alignPerformersToBottom: () => void;
  alignPerformersToCenter: () => void;
  alignPerformersToRight: () => void;
  handleNudgeRight: () => void;
  handleNudgeLeft: () => void;
  handleNudgeUp: () => void;
  handleNudgeDown: () => void;
  handleRotateClockwise: () => void;
  handleRotateCounterClockwise: () => void;
  applyEvenSpacing: (spacing: number) => void;

  // Keyboard
  handleKeyDown: (key: string) => void;

  // Export
  exportEngine: ExportEngine | null;
  exportCanvas: React.RefObject<HTMLCanvasElement>;

  // Page Management
  handleAddPageAtIndex: (index: number) => void;
  handleDeletePageAtIndex: (index: number) => void;

  // Loading State
  loading: boolean;
}

const FieldEditorContext = createContext<FieldEditorContextType | null>(null);

interface FieldEditorProviderProps {
  children: ReactNode;
}

export function FieldEditorProvider({ children }: FieldEditorProviderProps) {
  const { slug } = useParams();

  /** ---- Refs & States ---- */
  const [loading, setLoading] = useState(true);
  const [fullyLoaded, setFullyLoaded] = useState(false);

  /** ---- Hooks ---- */
  const { userData } = useUser(auth);
  const { fieldEditorData, setFieldEditorData } = useFieldEditor();
  const {
    drill,
    engine,
    forceViewRecreate,
    DrillCanvas,
    view,
    performerPaintingProps,
    setPerformerPaintingProps,
    viewOptions,
    updateViewOptions,
    confirmShape,
    cancelShape,
  } = useDrillView({
    slug: slug as string,
    userData,
    fieldEditorData,
  });

  const exportCanvas = useRef<HTMLCanvasElement>(null);
  const [exportEngine, setExportEngine] = useState<ExportEngine | null>(null);
  const snapshotUploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const collaborationReturn = useCollaboration(
    auth,
    drill,
    slug as string,
    engine,
  );

  const {
    collaboration,
    collaborationHistoryLoading,
    drillModifiedByCollaboration,
  } = collaborationReturn;

  const { drillDocumentData, updateDrillDocumentData } = useDrillDocument({
    drillId: slug as string,
    drill,
    collaboration,
    drillModifiedByCollaboration,
    collaborationHistoryLoading,
    updateViewOptions,
  });

  const {
    playbackState,
    setPlaybackState,
    handlePageChange,
    handleCountChange,
    handlePlayDrill,
    handleStopDrill,
  } = usePlayback(drill, view, drillModifiedByCollaboration);

  const {
    alignPerformersToLeft,
    alignPerformersToTop,
    alignPerformersHorizontally,
    alignPerformersToBottom,
    alignPerformersToCenter,
    alignPerformersToRight,
    handleNudgeRight,
    handleNudgeLeft,
    handleNudgeUp,
    handleNudgeDown,
    applyEvenSpacing,
    handleRotateClockwise,
    handleRotateCounterClockwise,
  } = usePerformerManipulation({
    drill: drill as Drill,
    viewOptions,
  });

  const { handleKeyDown } = useKeyboard(
    drill as Drill,
    view as View,
    playbackState,
    setPlaybackState,
    handlePlayDrill,
    handleStopDrill,
    handlePageChange,
    handleCountChange,
    setFieldEditorData,
    fieldEditorData,
    handleNudgeRight,
    handleNudgeLeft,
    handleNudgeUp,
    handleNudgeDown,
  );

  useEffect(() => {
    if (userData) {
      if (loading) {
        setLoading(false);
      }
    } else {
      if (!loading) {
        setLoading(true);
      }
    }
  }, [userData, collaboration, loading]);

  useEffect(() => {
    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Delete" || event.key === "Backspace") {
        const activeElement = document.activeElement;
        const isTypingInField =
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            (activeElement as HTMLElement).isContentEditable);

        if (
          !isTypingInField &&
          drill?.engine?.selectedPerformers &&
          drill.engine.selectedPerformers.length > 0
        ) {
          event.preventDefault();
        }
      }
      handleKeyDown(event.key);
    };

    window.addEventListener("keydown", keyHandler);
    return () => {
      window.removeEventListener("keydown", keyHandler);
    };
  }, [handleKeyDown, drill]);

  useEffect(() => {
    if (!loading && forceViewRecreate && !fullyLoaded) {
      setFullyLoaded(true);
      forceViewRecreate();
    }
  }, [loading, forceViewRecreate, fullyLoaded]);

  useEffect(() => {
    if (!view || !exportCanvas.current) return;

    const dpr = window.devicePixelRatio || 1;
    exportCanvas.current.width = exportCanvas.current.clientWidth / dpr;
    exportCanvas.current.height = exportCanvas.current.clientHeight / dpr;

    const drill = view.getDrill();

    drill.pages.forEach((page, index) => {
      const directions = page.getCustomData("directions") as string | undefined;

      const moveCounts = index > 0 ? (page.counts ?? 0) : 0;

      const moveMessage = `Move ${moveCounts} counts`;
      const tempoMessage = `Tempo ${page.tempo ?? 120} bpm`;

      const directionsMessage = directions ? directions : "";

      page.customMessage = [moveMessage, tempoMessage, directionsMessage]
        .filter(Boolean)
        .join("\n");
    });

    const exportEngine = new ExportEngine(
      drill,
      exportCanvas.current,
      undefined,
      view.getEngine().endZones,
      view.getEngine().hashType,
      52.5,
      false, // TODO: Make this dynamic based on user settings
    );

    setExportEngine(exportEngine);
  }, [fieldEditorData.showExportDialog, view, exportCanvas]);

  const handleAddPageAtIndex = useCallback(
    async (index: number) => {
      if (!drill) return;
      // toast.success("Adding page at index " + index);
      try {
        const lastPage =
          drill.pages.length > 0
            ? drill.pages[drill.pages.length - 1]
            : undefined;

        const lastPageTempo = lastPage?.tempo ?? 120;
        const page = new Page(drill, lastPage);
        page.counts = 8;
        page.tempo = lastPageTempo;

        drill.addPage(page, index);
        drill.selectPage(index);
      } catch (error) {
        console.error("Error in handleAddPageAtIndex:", error);
        toast.error("Error adding page " + index);
      }
    },
    [drill],
  );

  const handleDeletePageAtIndex = useCallback(
    async (index: number) => {
      if (!drill) return;
      try {
        drill.removePage(drill.pages[index]);
      } catch (error) {
        console.error("Error in handleDeletePageAtIndex:", error);
        toast.error("Error deleting page " + index);
      }
    },
    [drill],
  );

  // Canvas Snapshot Uploading
  const exportCanvasAsBlob = useCallback(
    async (canvasId: string = "drillCanvas"): Promise<Blob | null> => {
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
      if (!canvas) {
        console.warn(`Canvas with id "${canvasId}" not found`);
        return null;
      }

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, "image/png");
      });
    },
    [],
  );
  const uploadCanvasSnapshot = useCallback(async (): Promise<void> => {
    if (!slug || !drill) return;

    const canvasBlob = await exportCanvasAsBlob("drillCanvas");
    if (!canvasBlob) {
      console.warn("Failed to export canvas as blob");
      return;
    }

    try {
      const storage = getStorage();
      const fileName = `snapshots/${slug}/snapshot_${Date.now()}.png`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, canvasBlob);
      const downloadURL = await getDownloadURL(storageRef);

      const drillRef = doc(db, "designs", slug);
      await updateDoc(drillRef, {
        snapshotURL: downloadURL,
        updatedAt: new Date(),
      });
      updateDrillDocumentData({
        snapshotURL: downloadURL,
      });

      console.log("Canvas snapshot uploaded successfully:", downloadURL);
    } catch (error) {
      console.error("Error uploading canvas snapshot:", error);
    }
  }, [slug, drill, exportCanvasAsBlob, updateDrillDocumentData]);
  const debouncedUploadSnapshot = useCallback(() => {
    // Clear existing timeout
    if (snapshotUploadTimeoutRef.current) {
      clearTimeout(snapshotUploadTimeoutRef.current);
    }

    // Set new timeout (e.g., 5 seconds after last edit)
    snapshotUploadTimeoutRef.current = setTimeout(() => {
      uploadCanvasSnapshot();
    }, 15000); // 15 second debounce
  }, [uploadCanvasSnapshot]);

  useEffect(() => {
    if (!drill || !collaboration?.isConnected) return;

    if (drillModifiedByCollaboration !== undefined) {
      debouncedUploadSnapshot();
    }

    return () => {
      if (snapshotUploadTimeoutRef.current) {
        clearTimeout(snapshotUploadTimeoutRef.current);
      }
    };
  }, [
    drill,
    collaboration,
    drillModifiedByCollaboration,
    debouncedUploadSnapshot,
  ]);

  const contextValue: FieldEditorContextType = {
    // Field Editor State
    fieldEditorData,
    setFieldEditorData,

    // Drill View State
    drill,
    engine,
    view,
    DrillCanvas,
    performerPaintingProps,
    setPerformerPaintingProps,
    viewOptions,
    updateViewOptions,
    confirmShape,
    cancelShape,
    forceViewRecreate,
    uploadCanvasSnapshot,

    // Playback State
    playbackState,
    setPlaybackState,
    handlePageChange,
    handleCountChange,
    handlePlayDrill,
    handleStopDrill,

    // Collaboration State
    collaborationReturn,

    // Drill Document State
    drillDocumentData,
    updateDrillDocumentData,

    // User State
    userData,

    // Performer Manipulation
    alignPerformersToLeft,
    alignPerformersToTop,
    alignPerformersHorizontally,
    alignPerformersToBottom,
    alignPerformersToCenter,
    alignPerformersToRight,
    handleNudgeRight,
    handleNudgeLeft,
    handleNudgeUp,
    handleNudgeDown,
    handleRotateClockwise,
    handleRotateCounterClockwise,
    applyEvenSpacing,

    // Keyboard
    handleKeyDown,

    // Export
    exportEngine,
    exportCanvas,

    // Page Management
    handleAddPageAtIndex,
    handleDeletePageAtIndex,

    // Loading State
    loading,
  };

  return (
    <FieldEditorContext.Provider value={contextValue}>
      {loading ? (
        <>
          <MarketingNavbar />
          <LoadingSpinner />
        </>
      ) : (
        children
      )}
    </FieldEditorContext.Provider>
  );
}

export function useFieldEditorContext(): FieldEditorContextType {
  const context = useContext(FieldEditorContext);
  if (!context) {
    throw new Error(
      "useFieldEditorContext must be used within a FieldEditorProvider",
    );
  }
  return context;
}
